import { RBAdminEmail } from "../../../util/Common";
import { RBPayService } from "../../../api/RBPayService";
import { AuthService } from "../../../api/AuthService";
import { buildAcceptedMethodsWithRBPay } from "../../../util/rbPay";
import {
  RBPayNetworkConfiguration,
  RBPaySettingsState,
} from "../types/rbPaySettings.types";
import {
  cloneRBPaySettingsState,
  buildRBPayProfileDashboardModel,
  createDefaultRBPaySettingsState,
  deserializeWorkerProfile,
  hasWorkerProfileRecord,
  serializeWorkerProfile,
} from "../utils/rbPaySettings.mappers";

// Surface the backend message when present; otherwise a standard, user-safe
// fallback (axios's own error.message is generic, e.g. "Request failed with
// status code 400", so we don't use it for display). NestJS validation errors
// arrive as `message` arrays (strings, or class-validator objects with
// `constraints`) — flatten those into a readable, multi-line string.
const resolveApiErrorMessage = (error: any, fallback: string) => {
  const message = error?.response?.data?.message;

  if (Array.isArray(message)) {
    const parts = message
      .map((item: any) =>
        typeof item === "string"
          ? item
          : Object.values(item?.constraints || {}).join(", ")
      )
      .filter(Boolean);
    return parts.length ? parts.join("\n") : fallback;
  }

  return (typeof message === "string" && message) || fallback;
};

// A worker with no RB Pay profile yet returns 404 — treated as "not started"
// rather than an error so the first-time setup flow can proceed.
const isNotFoundError = (error: any) => error?.response?.status === 404;

interface GetWorkerRBPaySettingsArgs {
  account: any;
  industryMode?: string | null;
}

interface SaveWorkerRBPaySettingsArgs {
  account: any;
  settings: RBPaySettingsState;
  industryMode?: string | null;
}

interface SetWorkerRBPayEnabledArgs {
  account: any;
  enabled: boolean;
  industryMode?: string | null;
}

// RB Pay network configuration lives on the worker's network (account.networks[0]):
// `tempwisePortalUrl` (the View Statements destination) and `rbPayContactEmail`
// (the RB Pay support address). Fall back to the network's generic contactEmail,
// then localStorage / RBAdminEmail, only if the RB-Pay-specific value isn't set.
const resolveNetwork = (account: any) => account?.networks?.[0] || null;
const resolveContactEmail = (account: any) => {
  const network = resolveNetwork(account);
  return String(
    network?.rbPayContactEmail ||
      network?.contactEmail ||
      localStorage.getItem("contactEmail") ||
      RBAdminEmail
  );
};

export const rbPaySettingsService = {
  async getWorkerRBPaySettings({ account, industryMode }: GetWorkerRBPaySettingsArgs): Promise<RBPaySettingsState> {
    try {
      const profile = await RBPayService.getWorkerProfile();
      return deserializeWorkerProfile(profile, account, industryMode);
    } catch (error) {
      if (isNotFoundError(error)) {
        // No profile yet → first-time setup (default state).
        return deserializeWorkerProfile(null, account, industryMode);
      }
      throw new Error(resolveApiErrorMessage(error, "Unable to load RB Pay settings."));
    }
  },

  async saveWorkerRBPaySettings({ account, settings, industryMode }: SaveWorkerRBPaySettingsArgs): Promise<RBPaySettingsState> {
    const payload = serializeWorkerProfile(settings);
    // First submission creates the profile; subsequent edits patch it.
    const isExisting = hasWorkerProfileRecord(settings);

    try {
      const profile = isExisting
        ? await RBPayService.updateWorkerProfile(payload)
        : await RBPayService.createWorkerProfile(payload);
      return deserializeWorkerProfile(profile, account, industryMode);
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, "Unable to save RB Pay settings."));
    }
  },

  async setWorkerRBPayEnabled({ account, enabled, industryMode }: SetWorkerRBPayEnabledArgs): Promise<RBPaySettingsState> {
    const currentState = await this.getWorkerRBPaySettings({ account, industryMode });
    const dashboardModel = buildRBPayProfileDashboardModel(currentState);

    if (enabled && !dashboardModel.canEnable) {
      throw new Error("RB Pay can only be enabled once setup is complete and the RB Pay Status is Active.");
    }

    // Persist RB Pay enablement exactly like Stripe: add/remove "rb_pay" in
    // pharmacist.acceptedPaymentMethods via the worker-update endpoint
    // (account/pharmacist), preserving all other selected methods. The RB Pay
    // approval lifecycle lives on the rb-pay/worker-profile record — which we
    // deliberately do NOT touch here. Patching the full profile (the previous
    // behaviour) made the backend treat the toggle as a re-submission and reset the
    // approval status back to Pending.
    try {
      await AuthService.updateWorker({
        acceptedPaymentMethods: buildAcceptedMethodsWithRBPay(account, enabled),
      });
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, "Unable to update RB Pay. Please try again."));
    }

    // Optimistic next state: flip only `dashboard.enabled`, keep the approval status
    // exactly as it was. Callers refresh the account afterwards for the authoritative
    // value (mirrors the Stripe save flow).
    return {
      ...cloneRBPaySettingsState(currentState),
      dashboard: {
        ...currentState.dashboard,
        enabled,
      },
    };
  },

  async uploadWorkerRBPayDocument(_account: any, file: File) {
    // Real S3 upload via the worker void-cheque endpoint. The local object URL
    // drives an immediate preview for this session (on reload the GET returns a
    // presigned URL via buildWorkerVoidChequeDocument).
    try {
      const response = await RBPayService.uploadVoidCheque(file);
      const s3Key = String(response?.voidChequeS3Key || "");
      return {
        id: s3Key || `void-${Date.now()}`,
        name: file.name,
        type: file.type,
        size: file.size,
        previewUrl: URL.createObjectURL(file),
        uploadedAt: new Date().toISOString(),
        source: "s3" as const,
        s3Key,
      };
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, "Unable to upload document. Please try again."));
    }
  },

  async requestEmailVerificationCode(_account: any, email: string): Promise<void> {
    // Worker is identified server-side from the auth token; only the EMT email
    // is sent. The server tracks the pending OTP for this worker.
    try {
      await RBPayService.requestEmtVerification(email);
    } catch (error) {
      throw new Error(
        resolveApiErrorMessage(error, "Unable to send verification code. Please try again.")
      );
    }
  },

  async verifyEmailVerificationCode(_account: any, code: string): Promise<boolean> {
    // Verify needs only the code; the server matches it to the pending request.
    try {
      await RBPayService.verifyEmtVerification(code);
      return true;
    } catch (error) {
      throw new Error(
        resolveApiErrorMessage(error, "Verification code is invalid or expired. Please try again.")
      );
    }
  },

  async getRBPayStatementsUrl(account: any): Promise<string> {
    const config = await this.getRBPayNetworkConfiguration(account);
    return config.statementsUrl;
  },

  async getRBPayNetworkConfiguration(account: any): Promise<RBPayNetworkConfiguration> {
    const network = resolveNetwork(account);
    return {
      statementsUrl: String(network?.tempwisePortalUrl || ""),
      contactEmail: resolveContactEmail(account),
    };
  },

  buildInitialState(account: any, industryMode?: string | null) {
    return createDefaultRBPaySettingsState(account, industryMode);
  },
};
